/**
 * Which project actions each automatic trigger fires.
 *
 * Shared because the two triggers are fired from opposite sides — the web runs
 * the setup action right after it creates a worktree, the server runs the
 * archive actions when it folds a `thread.archived` event — and both sides have
 * to agree on which actions qualify.
 *
 * @module projectScripts
 */
import type { ProjectScript } from "@taskin-code/contracts";

/**
 * Only `terminal` actions can fire automatically: `chat` and `compose` need a
 * thread turn or a composer to land in, and neither exists at worktree creation
 * or at archive time. The editing dialog already forces both flags to false for
 * the other kinds — this is the second lock, for rows saved before that rule
 * existed.
 */
const firesAutomatically = (script: ProjectScript): boolean => script.kind === "terminal";

/** The `runOnWorktreeCreate` action of a project, if it has one. */
export function setupProjectScript(scripts: readonly ProjectScript[]): ProjectScript | null {
  return scripts.find((script) => script.runOnWorktreeCreate && firesAutomatically(script)) ?? null;
}

/**
 * Every action to run when a thread is archived, in the project's own order.
 *
 * Unlike the setup action there is no exclusivity: a project can queue a whole
 * teardown sequence, and the runner executes them one after another.
 */
export function archiveProjectScripts(
  scripts: readonly ProjectScript[],
): ReadonlyArray<ProjectScript> {
  return scripts.filter((script) => script.runOnThreadArchive && firesAutomatically(script));
}
